/* ================================================
   EUCALOO THEME — theme.js
   Core theme initialization, mobile menu, sticky header
   ================================================ */

(() => {
  'use strict';

  /* ---- Sticky Header ---- */
  const header = document.getElementById('SiteHeader');
  if (header) {
    if (header.classList.contains('site-header--sticky')) {
      document.body.classList.add('has-sticky-header');
    }
    const annBar = document.querySelector('.ann-bar');
    const onScroll = () => {
      const annH = annBar ? annBar.offsetHeight : 0;
      const offset = Math.max(0, annH - window.scrollY);
      header.style.top = offset + 'px';
      header.classList.toggle('scrolled', window.scrollY >= annH);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Defer initial call to after first paint so ann-bar height is available
    requestAnimationFrame(onScroll);
  }

  /* ---- Overlay Management ---- */
  const overlay = document.getElementById('SiteOverlay');
  function openOverlay(onClose) {
    overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    if (onClose) {
      const handler = () => { onClose(); overlay?.removeEventListener('click', handler); };
      overlay?.addEventListener('click', handler);
    }
  }
  function closeOverlay() {
    overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }
  window.EucalooOverlay = { open: openOverlay, close: closeOverlay };

  /* ---- Mobile Menu ---- */
  const mobileMenu   = document.getElementById('MobileMenu');
  const menuToggle   = document.getElementById('MobileMenuToggle');
  const menuClose    = document.getElementById('MobileMenuClose');

  function openMobileMenu() {
    resetPanels();
    mobileMenu?.classList.add('open');
    mobileMenu?.setAttribute('aria-hidden', 'false');
    menuToggle?.setAttribute('aria-expanded', 'true');
    openOverlay(closeMobileMenu);
  }
  function closeMobileMenu() {
    mobileMenu?.classList.remove('open');
    mobileMenu?.setAttribute('aria-hidden', 'true');
    menuToggle?.setAttribute('aria-expanded', 'false');
    closeOverlay();
  }

  menuToggle?.addEventListener('click', openMobileMenu);
  menuClose?.addEventListener('click', closeMobileMenu);

  // Panel drill-down navigation
  const panelStack = ['root'];

  function pushPanel(id) {
    const prevId = panelStack[panelStack.length - 1];
    const prevEl = mobileMenu?.querySelector(`[data-panel="${prevId}"]`);
    const nextEl = mobileMenu?.querySelector(`[data-panel="${id}"]`);
    if (!nextEl) return;
    prevEl?.classList.remove('is-active');
    prevEl?.classList.add('is-previous');
    nextEl.classList.add('is-active');
    panelStack.push(id);
  }

  function popPanel() {
    if (panelStack.length <= 1) return;
    const currentId = panelStack.pop();
    const prevId = panelStack[panelStack.length - 1];
    const currentEl = mobileMenu?.querySelector(`[data-panel="${currentId}"]`);
    const prevEl = mobileMenu?.querySelector(`[data-panel="${prevId}"]`);
    currentEl?.classList.remove('is-active');
    prevEl?.classList.remove('is-previous');
    prevEl?.classList.add('is-active');
  }

  function resetPanels() {
    panelStack.length = 0;
    panelStack.push('root');
    mobileMenu?.querySelectorAll('.mobile-menu__panel').forEach(p => {
      p.classList.remove('is-active', 'is-previous');
    });
    mobileMenu?.querySelector('[data-panel="root"]')?.classList.add('is-active');
  }

  mobileMenu?.addEventListener('click', e => {
    const trigger = e.target.closest('[data-open-panel]');
    const back = e.target.closest('[data-close-panel]');
    if (trigger) pushPanel(trigger.dataset.openPanel);
    else if (back) popPanel();
  });

  // Close on Escape
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closeMobileMenu();
      closeSearchDrawer();
      closeOverlay();
    }
  });

  /* ---- Search Drawer ---- */
  const searchDrawer = document.getElementById('SearchDrawer');
  const searchToggle = document.getElementById('SearchToggle');
  const searchClose  = document.getElementById('SearchClose');
  const searchInput  = document.getElementById('SearchInput');

  function openSearchDrawer() {
    // Close mobile menu if open
    closeMobileMenu();
    searchDrawer?.classList.add('open');
    searchDrawer?.setAttribute('aria-hidden', 'false');
    searchToggle?.setAttribute('aria-expanded', 'true');
    openOverlay(closeSearchDrawer);
    // Small delay so transition is visible before focusing
    setTimeout(() => searchInput?.focus(), 50);
  }
  function closeSearchDrawer() {
    searchDrawer?.classList.remove('open');
    searchDrawer?.setAttribute('aria-hidden', 'true');
    searchToggle?.setAttribute('aria-expanded', 'false');
    closeOverlay();
  }

  searchToggle?.addEventListener('click', openSearchDrawer);
  searchClose?.addEventListener('click', closeSearchDrawer);

  /* ---- Announcement Bar Dismiss ---- */
  document.querySelectorAll('.announcement-bar__close').forEach(btn => {
    btn.addEventListener('click', () => {
      const bar = btn.closest('.announcement-bar');
      bar?.style.setProperty('display', 'none');
      try { sessionStorage.setItem('eucaloo-ann-dismissed', '1'); } catch {}
    });
  });
  try {
    if (sessionStorage.getItem('eucaloo-ann-dismissed')) {
      document.querySelectorAll('.announcement-bar').forEach(b => b.style.display = 'none');
    }
  } catch {}

  /* ---- Account trigger (opens auth modal if not logged in) ---- */
  document.querySelectorAll('[data-account-trigger]').forEach(el => {
    el.addEventListener('click', e => {
      if (!document.body.classList.contains('customer-logged-in')) {
        e.preventDefault();
        window.EucalooAuth?.open('login');
      }
    });
  });

  /* ---- Lazy image fade-in ---- */
  if ('IntersectionObserver' in window) {
    const imgObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          imgObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '100px' });

    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.4s ease';
      img.addEventListener('load', () => img.style.opacity = '1');
      imgObserver.observe(img);
    });
  }

  /* ---- Gallery thumbnail switcher + swipe (product page) ---- */
  function galleryGoTo(gallery, src) {
    if (!gallery || !src) return;
    const mainImg = gallery.querySelector('.product-gallery__main img');
    if (mainImg) {
      // Clear srcset/sizes so the browser doesn't override our src change
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = src;
      // Also update <source> elements if wrapped in a <picture>
      const picture = mainImg.closest('picture');
      if (picture) picture.querySelectorAll('source').forEach(s => s.removeAttribute('srcset'));
    }
    gallery.querySelectorAll('.product-gallery__thumb').forEach(t => {
      t.classList.toggle('active', t.dataset.src === src);
    });
  }

  // Thumbnail clicks — event delegation so it works even after DOM updates
  document.addEventListener('click', e => {
    const thumb = e.target.closest('.product-gallery__thumb');
    if (!thumb) return;
    const gallery = thumb.closest('.product-gallery');
    galleryGoTo(gallery, thumb.dataset.src);
  });

  // Touch swipe on the main image
  document.querySelectorAll('.product-gallery__main').forEach(mainEl => {
    let startX = 0;
    mainEl.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    mainEl.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 40) return; // ignore small taps
      const gallery = mainEl.closest('.product-gallery');
      const thumbs  = Array.from(gallery?.querySelectorAll('.product-gallery__thumb') || []);
      if (!thumbs.length) return;
      const activeIdx = thumbs.findIndex(t => t.classList.contains('active'));
      const nextIdx   = diff > 0
        ? Math.min(activeIdx + 1, thumbs.length - 1)   // swipe left → next
        : Math.max(activeIdx - 1, 0);                  // swipe right → prev
      if (nextIdx !== activeIdx) galleryGoTo(gallery, thumbs[nextIdx].dataset.src);
    }, { passive: true });
  });

  /* ---- Quantity selector ---- */
  document.addEventListener('click', e => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;
    const wrap  = btn.closest('.qty-selector');
    const input = wrap?.querySelector('.qty-input');
    if (!input) return;
    const step = btn.dataset.direction === 'up' ? 1 : -1;
    const min  = parseInt(input.min || '1', 10);
    const max  = parseInt(input.max || '999', 10);
    const next = Math.max(min, Math.min(max, parseInt(input.value || '1', 10) + step));
    if (next !== parseInt(input.value, 10)) {
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---- Color swatch on product cards ---- */
  document.addEventListener('click', e => {
    const swatch = e.target.closest('.color-swatch[data-card-url]');
    if (!swatch) return;
    const card = swatch.closest('.product-card');
    if (!card) return;
    card.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
    swatch.classList.add('active');
    const img = card.querySelector('.product-card__media .img-primary');
    if (img && swatch.dataset.imgSrc) img.src = swatch.dataset.imgSrc;
  });

  console.log('[Eucaloo] Theme initialized');
})();
